import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import { Inline } from '@/design-system/inline/inline';
import { LoadingSpinner } from '@/design-system/loading/loading-spinner';
import { FinancialServiceProviderAdapters, OpenBankingConnectionStatus } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useOpenBankingAPI } from '../../../../../api/use-open-banking-api';
import { useUserInfo } from '../../../../../contexts/user-context/use-user-info';
import { ErrorSection } from '../../../../../design-system/error-section/error-section';
import { CompanyView, InstitutionView } from '../../../../../types/entity-types';
import { getInstitutionDetails } from '../../../utils/get-institution-details';
import { AccountConnectedErrorModal } from './account-connected-error-modal';
import { AccountConnectedSuccessModal } from './account-connected-success-modal';
import { ConnectAccountDialog } from './connect-account-dialog';
import { InstitutionList } from './institution-list';
import { PsuIdCollectionModal } from './psu-id-collection-modal';

type InstitutionBasicInfo = {
  name: string;
  logo: string;
};

type InstitutionListWrapperProps = {
  searchQuery: string;
  company: CompanyView;
};

// Institutions that require PSU-ID (Payment Service User Identifier)
// Deutsche Bank and Noris Bank require PSU-ID in format: branch_number (3 digits) + account_number (7 digits)
// See https://docs.yapily.com/pages/open-banking-flow/user-authorisation/psu_identifiers/
const PSU_ID_REQUIRED_INSTITUTIONS = ['deutschebank-pfb_de', 'deutschebank-noris_de'];

export const InstitutionListWrapper = ({ company, searchQuery }: InstitutionListWrapperProps) => {
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const { authenticatedInfo } = useUserInfo();
  const { enableHybridAIS, isStripeOpenBankingIntegrationLive, isAirwallexOpenBankingIntegrationLive } = useFlags();
  const companyCountry = company.legalEntity.country;
  const { data, isLoadingData, errorData, mutations } = useOpenBankingAPI(company);

  const [selectedInstitution, setSelectedInstitution] = useState<InstitutionView | null>(null);
  const [isPsuIdModalOpen, setIsPsuIdModalOpen] = useState(false);
  const [collectedPsuId, setCollectedPsuId] = useState<string | undefined>(undefined);
  const [connectionStatus, setConnectionStatus] = useState(data.connectionStatus);

  // Log auth link errors to Sentry
  useEffect(() => {
    if (errorData.authLinkError) {
      Sentry.captureException(errorData.authLinkError, {
        extra: {
          institutionName: selectedInstitution?.name,
          institutionId: selectedInstitution?.externalId,
          companyId: company.id,
        },
      });
    }
  }, [errorData.authLinkError, selectedInstitution, company.id]);

  if (isLoadingData.isLoadingInstitutions) {
    return (
      <Inline justify="center" align="center" className="pt-12">
        <LoadingSpinner />
      </Inline>
    );
  }

  if (!data.institutionsData) {
    Sentry.addBreadcrumb(errorData.institutionsError, {
      extra: {
        companyId: company.id,
        message: 'There was an error loading the list of institutions',
      },
    });

    toast.error('There was an error loading institutions');

    return <ErrorSection />;
  }

  const onInstitutionSelect = async (institution: InstitutionView) => {
    setSelectedInstitution(institution);
    const institutionDisplayData = getInstitutionDisplayData(institution);

    localStorage.setItem('selectedInstitution', JSON.stringify(institutionDisplayData));

    // Check if this institution requires PSU-ID
    const requiresPsuId = PSU_ID_REQUIRED_INSTITUTIONS.includes(institution.externalId);

    if (requiresPsuId) {
      // Show PSU-ID collection modal first
      setIsPsuIdModalOpen(true);
    } else {
      // Standard flow - get auth link immediately
      mutations.getAuthLink({
        companyId: company.id,
        institutionId: institution.externalId,
        userName: authenticatedInfo?.name || '',
      });
    }

    analytics.track('link_bank_flow_bank_selected', {
      bank_id: institution.externalId,
      bank_name: institution.name,
      provider: institution.provider,
      source_page: 'accounts',
    });

    return;
  };

  const onPsuIdSubmit = (psuId: string) => {
    setCollectedPsuId(psuId);
    setIsPsuIdModalOpen(false);

    // Now get auth link with PSU-ID
    if (selectedInstitution) {
      mutations.getAuthLink({
        companyId: company.id,
        institutionId: selectedInstitution.externalId,
        userName: authenticatedInfo?.name || '',
        psuId,
      });
    }
  };

  const closePsuIdModal = () => {
    setIsPsuIdModalOpen(false);
    setSelectedInstitution(null);
    setCollectedPsuId(undefined);
    localStorage.removeItem('selectedInstitution');
  };

  const closeModal = () => {
    setSelectedInstitution(null);
    setCollectedPsuId(undefined);

    localStorage.removeItem('selectedInstitution');

    analytics.track('link_bank_flow_cancelled', {
      bank_id: selectedInstitution?.externalId,
      bank_name: selectedInstitution?.name,
      step: 'consent_modal',
      source_page: 'accounts',
    });
  };

  const onConnectAccounts = () => {
    if (data.authLinkData?.link) {
      window.open(data.authLinkData.link, '_blank', 'noopener,noreferrer');

      analytics.track('link_bank_flow_confirmed', {
        bank_id: selectedInstitution?.externalId,
        bank_name: selectedInstitution?.name,
        selected_account_count: data.connectedCount,
        source_page: 'accounts',
      });
    }
  };

  // Filter institutions based on search query and feature flags
  const filteredInstitutions = data.institutionsData.institutions.filter((institution) => {
    const matchesSearch = institution.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (!enableHybridAIS && institution.provider === FinancialServiceProviderAdapters.GOCARDLESS) return false;
    if (!isStripeOpenBankingIntegrationLive && institution.provider === FinancialServiceProviderAdapters.STRIPE)
      return false;
    if (!isAirwallexOpenBankingIntegrationLive && institution.provider === FinancialServiceProviderAdapters.AIRWALLEX)
      return false;
    return matchesSearch;
  });

  const institutionData = localStorage.getItem('selectedInstitution');
  const cachedInstitution: InstitutionBasicInfo | null = institutionData ? JSON.parse(institutionData) : null;

  return (
    <div>
      {/* PSU-ID Collection Modal - shown first for Deutsche Bank/Noris Bank */}
      {selectedInstitution && isPsuIdModalOpen && (
        <PsuIdCollectionModal
          isOpen={isPsuIdModalOpen}
          onClose={closePsuIdModal}
          institutionName={getInstitutionDetails(selectedInstitution).name}
          institutionLogo={getInstitutionDetails(selectedInstitution).logo}
          onSubmit={onPsuIdSubmit}
        />
      )}

      {/* Connection Dialog - shown after PSU-ID is collected (if required) or immediately */}
      {selectedInstitution && companyCountry && !isPsuIdModalOpen && (
        <ConnectAccountDialog
          isOpen={!!selectedInstitution}
          closePopup={closeModal}
          institutionName={getInstitutionDetails(selectedInstitution).name}
          institutionLogo={getInstitutionDetails(selectedInstitution).logo}
          country={companyCountry}
          isInstitutionConnected={!!data.authLinkData?.link}
          isFetchingConnectionLink={isLoadingData.isFetchingAuthLink}
          connectionError={errorData.authLinkError}
          onConnectAccounts={onConnectAccounts}
          psuIdProvided={!!collectedPsuId}
          provider={selectedInstitution.provider}
        />
      )}

      <AccountConnectedSuccessModal
        isOpen={connectionStatus === OpenBankingConnectionStatus.CONNECTED}
        onClose={() => {
          mutations.clearUrlParams();
          navigate('/transactions');
        }}
        institutionName={cachedInstitution?.name}
        institutionLogo={cachedInstitution?.logo}
        accountsCount={data.connectedCount ?? undefined}
        onLinkAnotherAccount={() => {
          mutations.clearUrlParams();
          setConnectionStatus(null);
        }}
      />

      <AccountConnectedErrorModal
        isOpen={connectionStatus === OpenBankingConnectionStatus.DISCONNECTED}
        onOpenChange={() => {
          mutations.clearUrlParams();
          setConnectionStatus(null);
        }}
      />

      <InstitutionList institutions={filteredInstitutions} onSelectInstitution={onInstitutionSelect} />
    </div>
  );
};

const getInstitutionDisplayData = (institution: InstitutionView): InstitutionBasicInfo => {
  return getInstitutionDetails(institution);
};
